import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import GridLoader from '@hobs/components/Grid/GridLoader';
import ColumnMenu from '@hobs/components/Grid/ColumnMenu';
import { H2 } from '@hobs/components/Headings';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import Panel from '@hobs/components/Panel';
import { createLoadingSelector } from '@hobs/utils/loading';
import { formatLocal } from '@hobs/utils/formatWithTimeZone';
import InputLabel from '@hobs/components/InputLabel';
import Icon from '@hobs/components/Icon';
import { GhostButton } from '@hobs/components/Buttons';
import { DATETIME_FORMAT, PAGE_SIZES } from '@hobs/utils/constants';
import { getAuditLogItems } from '@hobs/actions/serviceRequestAuditLog';

import './AuditLogGridItem.scss';
import { getAuditLogItemFieldValue } from '@hobs/utils/getAuditLogFieldValue/getAuditLogItemFieldValue';

class AuditLogGridItem extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      auditLog: props.auditLog,
      auditLogItems: [],
      auditLogItemDataState: {
        take: 10,
        skip: 0,
        sort: [{ field: 'FieldName', dir: 'asc' }]
      }
    };
  }

  getDisplayDate = date => {
    return formatLocal(date, DATETIME_FORMAT);
  };

  dataStateChange = e => {
    this.setState(
      {
        auditLogItemDataState: e.dataState
      },
      () => {
        this.loadODataQuery();
      }
    );
  };

  loadODataQuery = auditLogId => {
    const { auditLog } = this.state;
    this.props
      .getAuditLogItems(
        `AuditLogItemQuery(${auditLogId ? auditLogId : auditLog.AuditLogId})`,
        this.state.auditLogItemDataState
      )
      .then(response => {
        if (response.success) {
          this.setState({
            auditLogItems: response
          });
        } else {
          this.setState({
            auditLogItems: []
          });
        }
      });
  };

  componentDidMount() {
    this.loadODataQuery();
  }

  componentWillUnmount() {
    this.setState({
      auditLogItems: []
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.auditLog.AuditLogId != this.props.auditLog.AuditLogId) {
      this.setState(
        {
          auditLog: this.props.auditLog,
          auditLogItemDataState: {
            ...this.state.auditLogItemDataState,
            skip: 0
          }
        },
        () => {
          this.loadODataQuery(this.props.auditLog.AuditLogId);
        }
      );
    }
  }

  closeDetails = () => {
    this.setState({
      auditLog: null,
      auditLogItemDataState: {
        ...this.state.auditLogItemDataState,
        skip: 0
      },
      auditLogItems: []
    });
    this.props.onClose();
  };

  render() {
    const { t, getPhaseTypeValue } = this.props;
    const { auditLogItems, auditLog } = this.state;
    return (
      <>
        {auditLog && (
          <div className="auditLog-item">
            <>
              <Panel>
                <GhostButton
                  className="close-button"
                  iconLeft={<Icon icon="Close" />}
                  onClick={this.closeDetails}
                />
                <div className="auditLog-item--details">
                  <H2>{t('Details')}</H2>
                  <div className="auditLog-item--details--row">
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('Date')}</InputLabel>
                      {this.getDisplayDate(auditLog.CreatedDate)}
                    </div>
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('User')}</InputLabel>
                      {auditLog.UserFullName}
                    </div>
                  </div>
                  <div className="auditLog-item--details--row">
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('Phase')}</InputLabel>
                      {getPhaseTypeValue(auditLog.PhaseTypeId)}
                    </div>
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('Party')}</InputLabel>
                      {auditLog.Party.Name}
                    </div>
                  </div>
                  <div className="auditLog-item--details--row">
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('Action')}</InputLabel>
                      {auditLog.ActionName}
                    </div>
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('Email')}</InputLabel>
                      {auditLog.Email}
                    </div>
                  </div>
                  <div className="auditLog-item--details--row">
                    <div className="auditLog-item--details--column">
                    </div>
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('Phone')}</InputLabel>
                      {auditLog.Phone || '-'}
                    </div>
                  </div>
                </div>
              </Panel>
              <Grid
                sortable={true}
                pageable={{
                  pageSizes: PAGE_SIZES
                }}
                {...this.state.auditLogItemDataState}
                {...auditLogItems}
                onDataStateChange={this.dataStateChange}
              >
                <GridColumn
                  field="FieldName"
                  title={t('Field name')}
                  columnMenu={ColumnMenu}
                />
                <GridColumn
                  field="OldValue"
                  title={t('Old')}
                  columnMenu={ColumnMenu}
                  cell={({ dataItem }) =>
                    getAuditLogItemFieldValue(
                      dataItem.OldValue,
                      dataItem.OldDateTimeValue,
                      dataItem.FieldName
                    )
                  }
                />
                <GridColumn
                  field="NewValue"
                  title={t('New')}
                  columnMenu={ColumnMenu}
                  cell={({ dataItem }) =>
                    getAuditLogItemFieldValue(
                      dataItem.NewValue,
                      dataItem.NewDateTimeValue,
                      dataItem.FieldName
                    )
                  }
                />
              </Grid>
              {this.props.isLoading && <GridLoader />}
            </>
          </div>
        )}
      </>
    );
  }
}

AuditLogGridItem.propTypes = {
  isLoading: PropTypes.bool,
  match: PropTypes.object,
  t: PropTypes.func,
  auditLog: PropTypes.object,
  getAuditLogItems: PropTypes.func,
  onClose: PropTypes.func,
  getActionTypeValue: PropTypes.func,
  getPhaseTypeValue: PropTypes.func
};

const oDataLoading = createLoadingSelector(['OData']);
const mapStateToProps = state => ({
  isLoading: oDataLoading(state)
});

const mapDispatchToProps = {
  getAuditLogItems
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(AuditLogGridItem))
);
